import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Spinner, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faEnvelope, faPhone, faBriefcase, faCheckCircle, faExclamationTriangle,
  faSearch, faSyncAlt, faFileCsv, faChevronLeft, faChevronRight,
  faSpinner, faTimes, faUserCircle, faBuilding, faTag, faCalendarAlt,
  faAnglesLeft, faAnglesRight, faTrashAlt, faCheckDouble
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { PlacementContext } from '../layout/CunstomEntretiens';

const API_BASE = `${API_CONFIG2.apiUrl}placement/api_placement.php`;

// ============================================================
// UTILITAIRE FETCH JSON
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('CandidatsAcceptesCache');
db.version(1).stores({
  candidats: 'key, data, timestamp',
  offres: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key, store = 'candidats', allowExpired = false) {
  try {
    const entry = await db[store].get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data, store = 'candidats') {
  try {
    await db[store].put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}
async function getStale(key, store = 'candidats') {
  return getCached(key, store, true);
}

// Fonction pour vider tout le cache
const clearCache = async () => {
  await db.candidats.clear();
  await db.offres.clear();
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const CandidatsAcceptes = () => {
  const context = useContext(PlacementContext) || {};
  const { refreshKey, setSource } = context;

  const [candidats, setCandidats] = useState([]);
  const [filteredCandidats, setFilteredCandidats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // État pour suivre les candidats déjà placés (set d'IDs)
  const [candidatsPlaces, setCandidatsPlaces] = useState(new Set());

  const [showModal, setShowModal] = useState(false);
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [offres, setOffres] = useState([]);
  const [selectedOffre, setSelectedOffre] = useState('');
  const [loadingOffres, setLoadingOffres] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offresError, setOffresError] = useState('');

  const searchRef = useRef(null);

  // ============================================================
  // RÉCUPÉRATION DES CANDIDATS
  // ============================================================
  const fetchCandidats = useCallback(async (forceRefresh = false, retryCount = 0) => {
    setLoading(true);
    setError('');

    try {
      const cacheKey = 'candidats_acceptes';

      if (!forceRefresh) {
        const cached = await getCached(cacheKey, 'candidats');
        if (cached) {
          // On suppose que les données contiennent un champ 'est_place'
          const places = new Set(cached.filter(c => c.est_place).map(c => c.id));
          setCandidatsPlaces(places);
          setCandidats(cached);
          setTotalRecords(cached.length);
          applyFilters(cached, searchTerm);
          setDataSource('cache');
          if (setSource) setSource('cache');
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`${API_BASE}?action=candidats-acceptes`);

      if (response.status === 429) {
        const stale = await getStale(cacheKey, 'candidats');
        if (stale) {
          const places = new Set(stale.filter(c => c.est_place).map(c => c.id));
          setCandidatsPlaces(places);
          setCandidats(stale);
          setTotalRecords(stale.length);
          applyFilters(stale, searchTerm);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast('429 – Utilisation du cache (peut être obsolète)', 'warning');
          setLoading(false);
          return;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
        setTimeout(() => fetchCandidats(forceRefresh, retryCount + 1), waitTime);
        return;
      }

      if (!response.ok) {
        const stale = await getStale(cacheKey, 'candidats');
        if (stale) {
          const places = new Set(stale.filter(c => c.est_place).map(c => c.id));
          setCandidatsPlaces(places);
          setCandidats(stale);
          setTotalRecords(stale.length);
          applyFilters(stale, searchTerm);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur réseau – Utilisation du cache`, 'warning');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        // On enrichit les données avec un flag 'est_place' si l'API ne le fournit pas
        // Ici on suppose que l'API renvoie un champ 'est_place' (0/1)
        const candidatsAvecPlacement = data.data.map(c => ({
          ...c,
          est_place: c.est_place === 1 || c.est_place === true
        }));
        const places = new Set(candidatsAvecPlacement.filter(c => c.est_place).map(c => c.id));
        setCandidatsPlaces(places);
        setCandidats(candidatsAvecPlacement);
        setTotalRecords(candidatsAvecPlacement.length);
        applyFilters(candidatsAvecPlacement, searchTerm);
        const source = forceRefresh ? 'live (forcé)' : 'live';
        setDataSource(source);
        if (setSource) setSource(source);
        await setCached(cacheKey, candidatsAvecPlacement, 'candidats');
        if (forceRefresh) {
          showToast('Données actualisées', 'success');
        }
        setError('');
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message);
      const stale = await getStale('candidats_acceptes', 'candidats');
      if (stale) {
        const places = new Set(stale.filter(c => c.est_place).map(c => c.id));
        setCandidatsPlaces(places);
        setCandidats(stale);
        setTotalRecords(stale.length);
        applyFilters(stale, searchTerm);
        setDataSource('cache (périmé)');
        if (setSource) setSource('cache (périmé)');
        showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
      } else {
        showToast(err.message, 'danger');
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, setSource]);

  // ============================================================
  // RÉCUPÉRATION DES OFFRES
  // ============================================================
  const fetchOffres = useCallback(async (forceRefresh = false) => {
    setLoadingOffres(true);
    setOffresError('');
    try {
      const cacheKey = 'offres_disponibles';

      if (!forceRefresh) {
        const cached = await getCached(cacheKey, 'offres');
        if (cached && Array.isArray(cached) && cached.length > 0) {
          setOffres(cached);
          setLoadingOffres(false);
          return;
        }
      }

      const uniqueParam = new Date().getTime();
      const url = `${API_BASE}?action=offres&all=1&unique=${uniqueParam}`;
      const data = await fetchJSON(url);

      if (data.status === 'success') {
        let offresData = data.data;
        if (!Array.isArray(offresData)) {
          offresData = offresData ? [offresData] : [];
        }
        setOffres(offresData);
        await setCached(cacheKey, offresData, 'offres');
        if (offresData.length === 0) {
          setOffresError('Aucune offre disponible.');
        }
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setOffresError(err.message);
      const stale = await getStale('offres_disponibles', 'offres');
      if (stale && Array.isArray(stale) && stale.length > 0) {
        setOffres(stale);
        showToast('Erreur – Utilisation du cache des offres', 'warning');
      } else {
        showToast('Erreur chargement offres: ' + err.message, 'danger');
      }
    } finally {
      setLoadingOffres(false);
    }
  }, []);

  // ============================================================
  // VIDER LE CACHE
  // ============================================================
  const handleClearCache = async () => {
    await clearCache();
    showToast('Cache vidé avec succès', 'success');
    fetchCandidats(true);
  };

  // ============================================================
  // FILTRES ET EFFETS
  // ============================================================
  const applyFilters = (data, search) => {
    if (!search.trim()) {
      setFilteredCandidats(data);
      return;
    }
    const lower = search.toLowerCase();
    const filtered = data.filter(c =>
      (c.nom && c.nom.toLowerCase().includes(lower)) ||
      (c.prenom && c.prenom.toLowerCase().includes(lower)) ||
      (c.email && c.email.toLowerCase().includes(lower))
    );
    setFilteredCandidats(filtered);
  };

  useEffect(() => {
    applyFilters(candidats, searchTerm);
    setCurrentPage(1);
  }, [searchTerm, candidats]);

  useEffect(() => {
    fetchCandidats(false);
  }, []);

  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchCandidats(true);
    }
  }, [refreshKey, fetchCandidats]);

  // ============================================================
  // TOAST UTILITY
  // ============================================================
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ show: false }), 5000);
  };

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (filteredCandidats.length === 0) {
      showToast('Aucune donnée à exporter', 'warning');
      return;
    }
    const headers = ['ID', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Déjà placé'];
    const rows = filteredCandidats.map(c => [
      c.id, c.nom, c.prenom, c.email, c.tel,
      candidatsPlaces.has(c.id) ? 'Oui' : 'Non'
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `candidats_acceptes_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // MODAL PLACEMENT
  // ============================================================
  const openPlacementModal = async (candidat) => {
    if (candidatsPlaces.has(candidat.id)) {
      showToast('Ce candidat est déjà placé', 'warning');
      return;
    }
    setSelectedCandidat(candidat);
    setSelectedOffre('');
    setOffresError('');
    if (offres.length === 0 && !loadingOffres) {
      await fetchOffres(false);
    }
    setShowModal(true);
  };

  const handlePlacementSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOffre) {
      showToast('Veuillez sélectionner une offre', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const data = await fetchJSON(`${API_BASE}?action=placement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_candidat: selectedCandidat.id,
          id_offre: parseInt(selectedOffre)
        })
      });
      if (data.status === 'success') {
        showToast('Placement enregistré avec succès !', 'success');
        setShowModal(false);
        // Marquer le candidat comme placé dans l'état local
        setCandidatsPlaces(prev => new Set(prev).add(selectedCandidat.id));
        // Mettre à jour le candidat dans la liste pour afficher le badge
        setCandidats(prev => prev.map(c =>
          c.id === selectedCandidat.id ? { ...c, est_place: true } : c
        ));
        // On met aussi à jour le cache (optionnel) : on pourrait recharger
        // Mais on préfère recharger pour synchroniser avec le serveur
        fetchCandidats(true);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const totalPages = Math.ceil(filteredCandidats.length / limit);
  const paginatedData = filteredCandidats.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const goToFirst = () => changePage(1);
  const goToLast = () => changePage(totalPages);

  // ============================================================
  // RENDU
  // ============================================================
  if (loading && !candidats.length) {
    return (
      <div className="candidats-container">
        <div className="skeleton-header" />
        <div className="skeleton-card" />
        <div className="skeleton-card" />
        <div className="skeleton-card" />
        <style>{`
          .skeleton-header {
            height: 60px;
            background: rgba(255,255,255,0.05);
            border-radius: 12px;
            margin-bottom: 20px;
            animation: pulse 1.5s infinite;
          }
          .skeleton-card {
            height: 80px;
            background: rgba(255,255,255,0.04);
            border-radius: 12px;
            margin-bottom: 12px;
            animation: pulse 1.5s infinite;
          }
          @keyframes pulse {
            0% { opacity: 0.4; }
            50% { opacity: 0.7; }
            100% { opacity: 0.4; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #4f8cff;
          --accent-dark-blue: #2563eb;
          --accent-glow: rgba(79, 140, 255, 0.25);
          --bg-start: #0d1b2a;
          --bg-end: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(79, 140, 255, 0.15);
          --text-primary: #e8edff;
          --text-secondary: #a5b4d9;
          --text-muted: #5e6f94;
          --radius: 20px;
          --shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
        }

        .candidats-container {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          min-height: 100vh;
          padding: clamp(0.8rem, 2vw, 2.5rem);
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
          margin-bottom: 1.8rem;
        }
        .glass-card:hover {
          border-color: rgba(79, 140, 255, 0.25);
          box-shadow: 0 16px 48px rgba(79, 140, 255, 0.05);
        }

        .monetra-btn {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.95rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(79, 140, 255, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.success {
          background: rgba(46, 204, 113, 0.12);
          color: #2ecc71;
          border: 1px solid rgba(46, 204, 113, 0.2);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 38px;
          justify-content: center;
        }
        .monetra-btn.clear-cache {
          background: rgba(245, 158, 11, 0.12);
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }
        .monetra-btn.clear-cache:hover:not(:disabled) {
          background: rgba(245, 158, 11, 0.25);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.85vw, 0.75rem);
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(79,140,255,0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(79,140,255,0.1);
          font-weight: 500;
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.12);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.1);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.12);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.1);
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 2px 2px 2px 20px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.1);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }
        .search-wrapper .clear-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          padding: 0 12px;
          cursor: pointer;
          font-size: 1rem;
          transition: 0.2s;
        }
        .search-wrapper .clear-btn:hover {
          color: var(--text-primary);
        }

        .candidates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.5rem;
          margin-top: 0.5rem;
        }

        .candidat-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 1.5rem 1.2rem;
          transition: all 0.3s cubic-bezier(0.2,0,0,1);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(4px);
        }
        .candidat-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .candidat-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(79, 140, 255, 0.2);
          transform: translateY(-6px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.4);
        }
        .candidat-card:hover::before {
          opacity: 1;
        }
        .candidat-card.place {
          border-left: 4px solid #2ecc71;
        }
        .candidat-card.place::before {
          background: linear-gradient(90deg, transparent, #2ecc71, transparent);
          opacity: 1;
        }

        .candidat-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .candidat-card .avatar {
          width: 52px; height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, rgba(79, 140, 255, 0.2), rgba(79, 140, 255, 0.05));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          color: var(--accent-blue);
          flex-shrink: 0;
          border: 2px solid rgba(79, 140, 255, 0.1);
        }
        .candidat-card .user-info {
          flex: 1;
          margin-left: 14px;
          min-width: 0;
        }
        .candidat-card .user-info .name {
          font-weight: 600;
          font-size: 1.1rem;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .candidat-card .user-info .badge-accepte {
          background: rgba(79, 140, 255, 0.15);
          color: var(--accent-blue);
          padding: 2px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .candidat-card .user-info .badge-place {
          background: rgba(46, 204, 113, 0.15);
          color: #2ecc71;
          padding: 2px 14px;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .candidat-card .user-info .details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-top: 6px;
        }
        .candidat-card .user-info .details .row-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        .candidat-card .user-info .details .row-item .icon {
          width: 18px;
          color: var(--text-muted);
        }

        .candidat-card .card-actions {
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid rgba(255,255,255,0.04);
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .candidat-card .card-actions .monetra-btn {
          min-width: 100px;
          justify-content: center;
        }

        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 1.5rem;
          padding-top: 1.2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-wrapper .info {
          color: var(--text-secondary);
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .pagination-wrapper .info strong {
          color: var(--text-primary);
        }
        .pagination-buttons {
          display: flex;
          gap: 4px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pagination-buttons button {
          padding: 6px 12px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.2s;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          min-height: 36px;
          cursor: pointer;
          min-width: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pagination-buttons button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .pagination-buttons button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-buttons button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
          box-shadow: 0 4px 12px rgba(79, 140, 255, 0.3);
        }
        .pagination-buttons .page-ellipsis {
          padding: 0 6px;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(79, 140, 255, 0.2);
          border-radius: 16px;
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.6);
          animation: slideIn 0.3s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: 0.2s;
        }
        .btn-close-custom:hover { color: #fff; }

        .modal-content {
          background: rgba(10, 20, 45, 0.95) !important;
          backdrop-filter: blur(30px) !important;
          -webkit-backdrop-filter: blur(30px) !important;
          border: 1px solid rgba(79, 140, 255, 0.12) !important;
          border-radius: 28px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 24px 60px rgba(0,0,0,0.8) !important;
        }
        .modal-header {
          border-bottom: 1px solid rgba(79, 140, 255, 0.06) !important;
          padding: 1.2rem 1.5rem !important;
        }
        .modal-header .modal-title {
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .modal-header .btn-close {
          filter: invert(1) brightness(200%) !important;
          opacity: 0.5 !important;
        }
        .modal-body {
          padding: 1.5rem !important;
        }
        .modal-footer {
          border-top: 1px solid rgba(79, 140, 255, 0.06) !important;
          padding: 1rem 1.5rem !important;
          gap: 10px;
        }
        .form-label {
          font-size: 0.9rem;
          color: var(--text-secondary);
          font-weight: 500;
        }
        .form-select, .form-control {
          background: rgba(255,255,255,0.04) !important;
          border: 1px solid rgba(255,255,255,0.06) !important;
          color: var(--text-primary) !important;
          border-radius: 14px !important;
          padding: 10px 14px !important;
          font-size: 1rem !important;
        }
        .form-select:focus, .form-control:focus {
          border-color: var(--accent-blue) !important;
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.06) !important;
          background: rgba(255,255,255,0.06) !important;
        }
        .form-select option {
          background: #0d1b2a !important;
          color: var(--text-primary) !important;
        }
        .btn-custom {
          padding: 8px 22px;
          border-radius: 40px;
          font-weight: 500;
          border: none;
          transition: 0.2s;
        }
        .btn-custom-secondary {
          background: rgba(255,255,255,0.06);
          color: var(--text-primary);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .btn-custom-secondary:hover {
          background: rgba(255,255,255,0.12);
        }
        .btn-custom-primary {
          background: var(--accent-blue);
          color: #fff;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .btn-custom-primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(79, 140, 255, 0.4);
        }

        .offre-reload-btn {
          background: transparent;
          border: none;
          color: var(--accent-blue);
          cursor: pointer;
          padding: 4px 8px;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: 0.2s;
        }
        .offre-reload-btn:hover {
          color: var(--text-primary);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .candidats-container { padding: 12px; }
          .glass-card { padding: 1rem; border-radius: 16px; }
          .flex-between {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .flex-between .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            justify-content: center;
          }
          .search-wrapper {
            min-width: unset;
            width: 100%;
          }
          .candidates-grid {
            grid-template-columns: 1fr;
          }
          .candidat-card .card-header {
            flex-wrap: wrap;
          }
          .candidat-card .card-actions {
            justify-content: stretch;
          }
          .candidat-card .card-actions .monetra-btn {
            flex: 1;
          }
          .pagination-wrapper {
            flex-direction: column;
            align-items: center;
          }
          .pagination-buttons {
            justify-content: center;
          }
          .modal-footer {
            flex-direction: column-reverse;
          }
          .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .candidat-card {
            padding: 1rem;
          }
          .candidat-card .user-info .name {
            font-size: 0.95rem;
          }
          .candidat-card .user-info .details .row-item {
            font-size: 0.8rem;
          }
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .pagination-buttons button {
            min-width: 30px;
            padding: 4px 8px;
            font-size: 0.7rem;
          }
        }
      `}</style>

      <div className="candidats-container">
        {/* En-tête */}
        <div className="flex-between mb-4">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              <FontAwesomeIcon icon={faUserCircle} className="me-2 text-primary" />
              Candidats acceptés
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              {filteredCandidats.length} candidat(s) – {candidatsPlaces.size} déjà placé(s)
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn" onClick={() => fetchCandidats(false)} disabled={loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={() => fetchCandidats(true)} disabled={loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn clear-cache" onClick={handleClearCache} disabled={loading}>
              <FontAwesomeIcon icon={faTrashAlt} /> Vider le cache
            </button>
            <button className="monetra-btn primary" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
          </div>
        </div>

        {/* Recherche et limite */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                ref={searchRef}
              />
              {searchTerm && (
                <button className="clear-btn" onClick={() => { setSearchTerm(''); searchRef.current?.focus(); }}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary small">Afficher</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'var(--text-primary)', borderRadius: '30px', padding: '4px 12px', minHeight: '40px' }}
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste */}
        {error && (
          <div className="glass-card text-center py-4 text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
            <p>{error}</p>
          </div>
        )}

        {filteredCandidats.length === 0 ? (
          <div className="glass-card text-center py-5">
            <FontAwesomeIcon icon={faUserCircle} size="3x" className="text-secondary mb-3" />
            <p className="text-secondary">Aucun candidat accepté trouvé.</p>
          </div>
        ) : (
          <div className="glass-card">
            <div className="candidates-grid">
              {paginatedData.map(candidat => {
                const estPlace = candidatsPlaces.has(candidat.id);
                return (
                  <div key={candidat.id} className={`candidat-card ${estPlace ? 'place' : ''}`}>
                    <div className="card-header">
                      <div className="avatar">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div className="user-info">
                        <div className="name">
                          {candidat.prenom} {candidat.nom}
                          <span className="badge-accepte">Accepté</span>
                          {estPlace && (
                            <span className="badge-place">
                              <FontAwesomeIcon icon={faCheckDouble} /> Déjà placé
                            </span>
                          )}
                        </div>
                        <div className="details">
                          <div className="row-item">
                            <span className="icon"><FontAwesomeIcon icon={faEnvelope} /></span>
                            {candidat.email || '—'}
                          </div>
                          <div className="row-item">
                            <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                            {candidat.tel || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-actions">
                      {estPlace ? (
                        <button className="monetra-btn success" disabled>
                          <FontAwesomeIcon icon={faCheckDouble} /> Déjà placé
                        </button>
                      ) : (
                        <button className="monetra-btn primary" onClick={() => openPlacementModal(candidat)}>
                          <FontAwesomeIcon icon={faBriefcase} /> Placer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <div className="info">
                  <strong>Page {currentPage}</strong> sur {totalPages} ({filteredCandidats.length} enregistrements)
                </div>
                <div className="pagination-buttons">
                  <button onClick={goToFirst} disabled={currentPage === 1} aria-label="Première page">
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  <button onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1} aria-label="Page précédente">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  {[...Array(totalPages).keys()].map(num => {
                    const p = num + 1;
                    if (
                      p === 1 ||
                      p === totalPages ||
                      (p >= currentPage - 1 && p <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={p}
                          className={p === currentPage ? 'active' : ''}
                          onClick={() => changePage(p)}
                          aria-label={`Page ${p}`}
                        >
                          {p}
                        </button>
                      );
                    }
                    if (p === currentPage - 2 || p === currentPage + 2) {
                      return <span key={p} className="page-ellipsis">…</span>;
                    }
                    return null;
                  })}
                  <button onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Page suivante">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  <button onClick={goToLast} disabled={currentPage === totalPages} aria-label="Dernière page">
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL DE PLACEMENT */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faBriefcase} className="text-primary me-2" />
            Placer le candidat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCandidat && (
            <div>
              <div className="mb-3" style={{ background: 'rgba(255,255,255,0.03)', padding: '12px 16px', borderRadius: '12px' }}>
                <p className="mb-1"><strong>Candidat :</strong> {selectedCandidat.prenom} {selectedCandidat.nom}</p>
                <p className="mb-0"><strong>Email :</strong> {selectedCandidat.email || '—'}</p>
              </div>

              <Form onSubmit={handlePlacementSubmit}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label>Offre d'emploi</Form.Label>
                    <button
                      type="button"
                      className="offre-reload-btn"
                      onClick={() => fetchOffres(true)}
                      disabled={loadingOffres}
                    >
                      <FontAwesomeIcon icon={faSyncAlt} spin={loadingOffres} />
                      {loadingOffres ? 'Chargement...' : 'Rafraîchir'}
                    </button>
                  </div>
                  <Form.Select
                    value={selectedOffre}
                    onChange={(e) => setSelectedOffre(e.target.value)}
                    required
                    disabled={loadingOffres || submitting}
                  >
                    <option value="">Sélectionner une offre</option>
                    {loadingOffres ? (
                      <option disabled>Chargement des offres...</option>
                    ) : (
                      offres.map(offre => (
                        <option key={offre.id} value={offre.id}>
                          {offre.nomEntreprise || 'Entreprise'} – {offre.titre || offre.description}
                          {offre.type_contrat ? ` (${offre.type_contrat})` : ''}
                        </option>
                      ))
                    )}
                  </Form.Select>
                  {offresError && (
                    <div className="text-danger small mt-1">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      {offresError}
                    </div>
                  )}
                  {!loadingOffres && offres.length === 0 && !offresError && (
                    <div className="text-warning small mt-1">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                      Aucune offre disponible. Veuillez en créer une d'abord.
                    </div>
                  )}
                </Form.Group>
                <div className="d-flex justify-content-end gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
                  <Button variant="secondary" className="btn-custom btn-custom-secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                    Annuler
                  </Button>
                  <Button variant="success" className="btn-custom btn-custom-primary" type="submit" disabled={submitting || loadingOffres || offres.length === 0}>
                    {submitting ? <><FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...</> : 'Enregistrer le placement'}
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default CandidatsAcceptes;